import {
  MessageSquare, ThumbsUp, Send, Globe2, ChevronDown, MoreHorizontal,
  Truck, Route, Package, Image as ImageIcon, BarChart3, Phone,
  Sparkles, Users, TrendingUp, TrendingDown, Minus, Snowflake, Leaf,
  Building2, MapPin, Radio, HelpCircle, Info, Handshake, Hash,

} from "lucide-react";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useHubFeeds, type HubFeedChannel } from "@/hooks/hub/useHubFeeds";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import mikePhoto from "@/assets/mike-rodriguez.png";
import sarahPhoto from "@/assets/sarah-l-avatar.jpg";
import davidPhoto from "@/assets/david-torres.jpg";
import bethPhoto from "@/assets/beth-reynolds.jpg";
import carlosPhoto from "@/assets/carlos-trevino.jpg";
import sandraPhoto from "@/assets/sandra-williams.jpg";
import marcusPhoto from "@/assets/marcus-johnson.jpg";
import rachelPhoto from "@/assets/rachel-adams.jpg";
import { usePageMeta } from "@/hooks/usePageMeta";
import { HubOnboardingModal } from "@/components/social/HubOnboardingModal";
import { HubFeed } from "@/components/social/HubFeed";
import { HubAvatar } from "@/components/social/HubAvatar";
import { HubAdSlot } from "@/components/social/HubAdSlot";
import { useAuth } from "@/contexts/AuthContext";
import { useHubSettings } from "@/hooks/hub/useHubSettings";
import { useHubIdentities } from "@/hooks/hub/useHubIdentities";
import { useHubMyStats } from "@/hooks/hub/useHubMyStats";
import { hubBadgeReplacesRole, hubBadgeStyle } from "@/lib/hub/hubBadges";
import { hubMemberSinceLabel, hubTierForPosts } from "@/lib/hub/hubTiers";
import { useHubRecentlyActive, hubHasActivity, type HubRecentlyActiveMember } from "@/hooks/hub/useHubRecentlyActive";
import { relativeTime } from "@/lib/hub/relativeTime";
import { useHubRecentPosts, hubPostSnippet, type HubRecentPost } from "@/hooks/hub/useHubRecentPosts";
import { useHubRecentComments, hubCommentSnippet, type HubRecentComment } from "@/hooks/hub/useHubRecentComments";
import { useHubTrendingTags } from "@/hooks/hub/useHubTrendingTags";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";



/* ── Static mockup data ─────────────────────────────── */

const QUICK_SHARE = [
  {
    icon: Users,
    label: "Ask the Network",
    tone: "teal" as const,
    contentType: "general" as const,
    body: "Question for the network:\n\n",
  },
  {
    icon: HelpCircle,
    label: "Ask LaneRunner",
    tone: "blue" as const,
    contentType: "general" as const,
    body: "Question for the LaneRunner team:\n\n",
  },
  {
    icon: Sparkles,
    label: "Feature Request",
    tone: "blue" as const,
    contentType: "general" as const,
    body: "Feature request / feedback:\n\nWhat I'd love to see: \nWhy it helps: ",
  },
];











const COMPOSER_ACTIONS = [
  { icon: Truck, label: "Capacity Update" },
  { icon: Route, label: "Lane Request" },
  { icon: Package, label: "Shipment Need" },
  { icon: ImageIcon, label: "Photo/Doc" },
  { icon: BarChart3, label: "Start Poll" },
];



const POSTS = [
  {
    author: "Mike Rodriguez", org: "Prime Trucking", role: "Carrier", time: "2h ago", avatar: mikePhoto,
    body: ["Empty reefer in McAllen Thursday morning. Looking for Northeast freight.", "53ft reefer, food-grade, team available."],
    tags: [
      { icon: Route, text: "McAllen, TX → Northeast (NY/NJ/PA)" },
      { icon: Snowflake, text: "53ft Reefer" },
      { icon: Users, text: "Team" },
      { icon: Leaf, text: "Food Grade" },
    ],
    likes: 24, comments: 8, commentText: "Sarah L., David T. and 22 others", commentCount: "8 Comments",
    primary: { label: "Start Call", icon: Phone }, secondary: "View Profile",
    faces: [sarahPhoto, davidPhoto, carlosPhoto],
  },
  {
    author: "FreshWay Produce", org: null, role: "Shipper", time: "4h ago", initials: "FW",
    body: ["Looking for brokers experienced on Nogales → Midwest produce lanes for next week.", "Loads of romaine and berries. Consistent volume through July."],
    tags: [
      { icon: Route, text: "Nogales, AZ → Midwest (IL/OH/MO)" },
      { icon: Snowflake, text: "Reefer" },
      { icon: Leaf, text: "Produce" },
    ],
    likes: 18, comments: 6, commentText: "Mike R., Danielle H. and 17 others", commentCount: "6 Comments",
    secondary: "View Opportunity",
    faces: [mikePhoto, bethPhoto, rachelPhoto],
  },
  {
    author: "Sarah Lopez", org: "Velocity Logistics", role: "Broker", time: "1d ago", avatar: sarahPhoto,
    body: ["Closed a great TX → GA run this week for a customer. Shoutout to Carlos for the smooth backhaul!"],
    tags: [
      { icon: Route, text: "Dallas, TX → Atlanta, GA" },
      { icon: Package, text: "Dry Van" },
      { icon: Handshake, text: "Great Partner" },
    ],
    likes: 41, comments: 5, commentText: "Carlos T., Marcus J. and 38 others", commentCount: "5 Comments",
    secondary: "View Profile",
    faces: [carlosPhoto, marcusPhoto, davidPhoto],
  },
];

/* ── Small building blocks ──────────────────────────── */

function Avatar({ src, initials, size = 40, ring }: { src?: string | null; initials?: string; size?: number; ring?: boolean }) {
  if (src) {
    return (
      <img
        src={src}
        alt=""
        style={{ height: size, width: size }}
        className={cn("rounded-full object-cover flex-shrink-0", ring && "ring-2 ring-brand-teal/40")}
      />
    );
  }
  return (
    <div
      style={{ height: size, width: size }}
      className="rounded-full flex-shrink-0 flex items-center justify-center bg-brand-teal-deep text-white text-[11px] font-bold"
    >
      {initials}
    </div>
  );
}

function Card({ className, children }: { className?: string; children: React.ReactNode }) {
  return (
    <section className={cn("rounded-xl border border-border bg-card", className)} style={{ boxShadow: "var(--shadow-card)" }}>
      {children}
    </section>
  );
}

function CardHead({ title, action, hint, onAction }: { title: string; action?: string; hint?: boolean; onAction?: () => void }) {
  return (
    <div className="flex items-center justify-between px-4 pt-3.5 pb-2.5">
      <h3 className="text-[13px] font-bold text-foreground">{title}</h3>
      {hint ? <HelpCircle className="h-3.5 w-3.5 text-muted-foreground" /> : null}
      {action ? <button type="button" onClick={onAction} className="text-[11px] font-semibold text-brand-teal-deep hover:underline">{action}</button> : null}
    </div>
  );
}

/* ── Recently Active (REAL Hub activity — styling unchanged) ── */

function hubShortName(name: string): string {
  return name.trim().split(/\s+/)[0] || name;
}

function hubActiveLabel(m: HubRecentlyActiveMember): string {
  return hubHasActivity(m) ? `Active ${relativeTime(m.last_active_at)}` : "New member";
}

function RecentlyActiveCard() {
  const { data } = useHubRecentlyActive(30);
  const [open, setOpen] = useState(false);
  const members = data ?? [];
  const shown = members.slice(0, 5);
  const overflow = Math.max(0, members.length - shown.length);

  return (
    <>
      <Card>
        <CardHead title="Recently Active" action="View all" onAction={() => setOpen(true)} />
        <div className="flex items-start gap-3 px-4 pb-4">
          {shown.map((m) => (
            <div key={m.user_id} className="flex flex-col items-center gap-1 min-w-0">
              <div className="relative">
                <HubAvatar username={m.display_name} avatarPath={m.avatar_path} size="md" className="h-[38px] w-[38px] text-[11px]" />
              </div>
              <p className="text-[10px] font-semibold text-foreground truncate max-w-[52px]">{hubShortName(m.display_name)}</p>
              <p className="text-[9px] text-muted-foreground truncate max-w-[52px]">{hubActiveLabel(m)}</p>
            </div>
          ))}
          {overflow > 0 ? (
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="flex h-[38px] w-[38px] flex-shrink-0 items-center justify-center rounded-full border border-dashed border-border text-[10px] font-bold text-muted-foreground"
            >
              +{overflow}
            </button>
          ) : null}
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-[15px]">Recently Active</DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] space-y-3 overflow-y-auto pr-1">
            {members.map((m) => (
              <div key={m.user_id} className="flex items-center gap-2.5">
                <HubAvatar username={m.display_name} avatarPath={m.avatar_path} size="md" className="h-[34px] w-[34px] text-[11px]" />
                <div className="min-w-0 flex-1">
                  <p className="text-[12px] font-semibold text-foreground truncate">
                    {m.display_name}
                    <span className="ml-1.5 font-normal text-muted-foreground">@{m.handle}</span>
                  </p>
                  <p className="text-[10.5px] text-muted-foreground">{hubActiveLabel(m)}</p>
                </div>
              </div>
            ))}
            {members.length === 0 ? (
              <p className="text-[11.5px] text-muted-foreground">No Hub members yet.</p>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}


/* ── Recent Activity (REAL latest Hub posts — display only) ── */

function RecentActivityCard() {
  const { data } = useHubRecentPosts(5);
  const posts = (data ?? []).slice(0, 5);
  const identities = useHubIdentities(posts.map((p) => p.author_id));

  const rowText = (p: HubRecentPost, max: number) => {
    const name = identities.map.get(p.author_id)?.display_name || "LR Member";
    return `${hubShortName(name)}: ${hubPostSnippet(p.body, max)}`;
  };

  return (
    <Card>
      <CardHead title="Recent Activity" />
      <div className="space-y-3 px-4 pb-4">
        {posts.map((p) => (
          <div key={p.id} className="flex w-full items-center gap-2.5 text-left">
            <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md bg-brand-teal-subtle">
              <MessageSquare className="h-3.5 w-3.5 text-brand-teal-deep" />
            </div>
            <span className="w-[62px] flex-shrink-0 text-[10.5px] font-bold text-brand-teal-deep">
              {relativeTime(p.created_at)}
            </span>
            <span className="min-w-0 flex-1 truncate text-[11px] text-muted-foreground">
              {p.feed?.name ? <span className="font-semibold uppercase">{p.feed.name} · </span> : null}
              {rowText(p, 60)}
            </span>
          </div>
        ))}
        {posts.length === 0 ? (
          <p className="text-[11px] text-muted-foreground">No Hub posts yet.</p>
        ) : null}
      </div>
    </Card>
  );
}

/* ── Trending Tags (REAL hub_posts.tags aggregate — Hub-isolated) ── */

function TrendingTagsCard() {
  const { data, isLoading } = useHubTrendingTags(7, 5);
  const rows = data ?? [];

  return (
    <Card>
      <CardHead title="Trending Tags" />
      <div className="px-4 pb-4">
        <div className="grid grid-cols-[1fr_auto_auto] gap-x-3 border-b border-border pb-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          <span>Topic</span><span>Activity</span><span className="text-right">Trend</span>
        </div>
        {rows.length === 0 ? (
          <p className="py-3 text-[11.5px] text-muted-foreground">
            {isLoading ? "Loading…" : "Not enough activity yet"}
          </p>
        ) : (
          rows.map((t) => {
            const TrendIcon = t.trend === "up" ? TrendingUp : t.trend === "down" ? TrendingDown : Minus;
            const tone =
              t.trend === "up" ? "text-success" : t.trend === "down" ? "text-danger" : "text-muted-foreground";
            return (
              <div key={t.tag} className="grid grid-cols-[1fr_auto_auto] items-center gap-x-3 py-2 text-[11.5px]">
                <span className="flex min-w-0 items-center gap-2 truncate font-semibold text-foreground">
                  <Hash className="h-3.5 w-3.5 flex-shrink-0 text-brand-teal-deep" />
                  <span className="truncate">{t.tag}</span>
                </span>
                <span className="inline-flex items-center gap-1 text-muted-foreground tabular-nums">
                  <MessageSquare className="h-3 w-3" /> {t.activity}
                </span>
                {t.trend === "new" ? (
                  <span className="inline-flex items-center justify-end gap-1 font-semibold text-muted-foreground">
                    New
                  </span>
                ) : (
                  <span className={cn("inline-flex items-center justify-end gap-1 font-semibold tabular-nums", tone)}>
                    <TrendIcon className="h-3 w-3" /> {t.label}
                  </span>
                )}
              </div>
            );
          })
        )}
      </div>
    </Card>
  );
}

/* ── Recent Comments (REAL latest Hub comments — display only, Hub-isolated) ── */


function RecentCommentsCard() {
  const { data } = useHubRecentComments(5);
  const comments = (data ?? []).slice(0, 5);

  return (
    <Card>
      <CardHead title="Recent Comments" />
      <div className="space-y-3 px-4 pb-4">
        {comments.map((c: HubRecentComment) => (
          <div key={c.id} className="flex items-start gap-2.5">
            <HubAvatar
              username={c.author?.display_name || "Member"}
              avatarPath={c.author?.avatar_path ?? null}
              size="sm"
              className="h-8 w-8 text-[10px]"
            />
            <div className="min-w-0 flex-1">
              <p className="text-[11.5px] font-semibold text-foreground truncate">
                {c.author?.display_name || "LR Member"}
              </p>
              <p className="text-[10px] text-muted-foreground truncate">
                {hubCommentSnippet(c.body, 55)}
              </p>
            </div>
            <span className="flex-shrink-0 text-[10px] text-muted-foreground">
              {relativeTime(c.created_at)}
            </span>
          </div>
        ))}
        {comments.length === 0 ? (
          <p className="text-[11px] text-muted-foreground">No comments yet</p>
        ) : null}
      </div>
    </Card>
  );
}



function Chip({ icon: Icon, text }: { icon: any; text: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-md bg-brand-teal-subtle px-2 py-1 text-[11px] font-medium text-brand-teal-deep">
      <Icon className="h-3 w-3 flex-shrink-0" />
      {text}
    </span>
  );
}

function VerifiedDot() {
  return <span className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-full bg-success text-white text-[8px] font-bold">✓</span>;
}

/* ── My Hub profile card (real, logged-in user) ─────── */

function MyHubProfileCard() {
  const { user } = useAuth();
  const { data: settings } = useHubSettings();
  const { map } = useHubIdentities([user?.id]);
  const { data: stats } = useHubMyStats();

  const identity = user?.id ? map.get(user.id) ?? null : null;
  const name = identity?.display_name || settings?.display_name || "Your Hub profile";
  const handle = identity?.handle || settings?.handle || null;
  const badge = hubBadgeStyle(identity?.hub_badge);
  const role = identity?.role ?? null;
  const showRole = !!role && !hubBadgeReplacesRole(identity?.hub_badge);
  const posts = stats?.posts ?? 0;
  const tier = hubTierForPosts(posts);

  return (
    <Card className="p-4">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <HubAvatar username={name} avatarPath={identity?.avatar_path ?? null} size="lg" className="h-[52px] w-[52px] text-[15px]" />
          <div className="min-w-0">
            <p className="text-[15px] font-bold text-foreground truncate">{name}</p>
            <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
              {handle ? <span className="text-[11px] text-muted-foreground">@{handle}</span> : null}
              {badge ? (
                <span title={badge.title} className={cn("rounded-md px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide", badge.className)}>
                  {badge.label}
                </span>
              ) : null}
              {showRole ? (
                <span className="rounded-md bg-brand-teal-subtle px-1.5 py-0.5 text-[10px] font-bold uppercase text-brand-teal-deep">{role}</span>
              ) : null}
            </div>
            <span
              title={tier.title}
              className={cn("mt-1.5 inline-block rounded-md px-1.5 py-0.5 text-[10px] font-bold", tier.className)}
            >
              {tier.label}
            </span>
          </div>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-2 text-center">
        {[
          { v: posts, l: "Posts" },
          { v: stats?.comments ?? 0, l: "Comments" },
          { v: hubMemberSinceLabel(settings?.created_at), l: "Member Since" },
        ].map((s) => (
          <div key={s.l}>
            <p className="text-[15px] font-bold text-brand-teal-deep tabular-nums leading-none">{s.v}</p>
            <p className="mt-1 text-[10px] text-muted-foreground">{s.l}</p>
          </div>
        ))}
      </div>
    </Card>
  );
}

/* ── Feeds navigation (topic channels) ──────────────── */

function FeedsNavCard({
  feeds,
  selectedSlug,
  onSelect,
}: {
  feeds: HubFeedChannel[];
  selectedSlug: string | null;
  onSelect: (slug: string | null) => void;
}) {
  const items: { slug: string | null; name: string }[] = [
    { slug: null, name: "Home" },
    ...feeds.map((f) => ({ slug: f.slug, name: f.name })),
  ];

  return (
    <Card>
      <CardHead title="Feeds" />
      <div className="space-y-1 px-3 pb-3.5">
        {items.map((item) => {
          const active = selectedSlug === item.slug;
          return (
            <button
              key={item.slug ?? "home"}
              type="button"
              onClick={() => onSelect(item.slug)}
              aria-current={active ? "true" : undefined}
              className={cn(
                "flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-[12px] font-semibold transition-colors",
                active
                  ? "bg-brand-teal-subtle text-brand-teal-deep"
                  : "text-foreground hover:bg-muted",
              )}
            >
              {item.slug === null ? (
                <Radio className="h-4 w-4 flex-shrink-0" />
              ) : (
                <Hash className="h-4 w-4 flex-shrink-0" />
              )}
              <span className="truncate">{item.name}</span>
            </button>
          );
        })}
      </div>
    </Card>
  );
}

function FeedHeaderCard({ feed }: { feed: HubFeedChannel | null }) {
  const name = feed?.name ?? "Home";
  const description = feed?.description ?? "Recent posts from every feed.";
  const adminOnly = !!feed && feed.posting_role !== "everyone";

  return (
    <Card className="px-4 py-3">
      <h2 className="text-[14px] font-bold text-foreground">{name}</h2>
      <p className="mt-0.5 text-[11.5px] text-muted-foreground">{description}</p>
      {adminOnly ? (
        <p className="mt-1.5 inline-flex items-center gap-1.5 rounded-md bg-brand-teal-subtle px-2 py-1 text-[10.5px] font-semibold text-brand-teal-deep">
          <Info className="h-3 w-3" /> Posting is limited to the LaneRunner team
        </p>
      ) : null}
    </Card>
  );
}

/* ── Page ────────────────────────────────────────────── */


export default function SocialHubPage() {
  usePageMeta({ title: "LR Hub | LaneRunner", description: "Your freight network — connect, share capacity, and read the market." });
  const [composerPrefill, setComposerPrefill] = useState<{ body: string; content_type: string } | null>(null);

  // Feed selection (Home = null) mirrored to ?feed=slug so views are linkable.
  const { feeds, bySlug } = useHubFeeds();
  const [searchParams, setSearchParams] = useSearchParams();
  const paramSlug = searchParams.get("feed");
  const selectedFeed = paramSlug ? bySlug.get(paramSlug) ?? null : null;
  const selectedFeedSlug = selectedFeed?.slug ?? null;

  // Post targeted from the Recent Activity box: Home shows all feeds, so we
  // clear the feed filter and let HubFeed scroll to it.
  const [focusPostId, setFocusPostId] = useState<string | null>(null);
  const openPost = (postId: string) => {
    selectFeed(null);
    setFocusPostId(postId);
  };

  const selectFeed = (slug: string | null) => {

    const next = new URLSearchParams(searchParams);
    if (slug) next.set("feed", slug);
    else next.delete("feed");
    setSearchParams(next, { replace: true });
  };



  return (
    <div className="relative -mb-2.5 h-full flex flex-col overflow-x-clip lg:-mx-3">
      {/* First-time LR Hub identity onboarding (Hub-isolated: hub_settings only). */}
      <HubOnboardingModal />

      {/* Coming Soon overlay - TEMPORARILY DISABLED
      <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/30">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-foreground">Coming Soon</h2>
          <p className="text-sm text-muted-foreground mt-2">LR Hub is under development</p>
        </div>
      </div>
      */}
      {/* Blurred content - TEMPORARILY DISABLED
      <div className="pointer-events-none select-none flex-1 flex flex-col" style={{ filter: 'blur(4px)' }}>
      */}
      {/* Page-scoped scroll: on lg+ this wrapper does NOT scroll — only the
          center feed column does. Below lg it scrolls normally as one column. */}
      {/* Below lg this is NOT a scroller — the AppShell main scrolls, so there
          is no nested scroll-chaining wobble on phones. */}
      <div className="flex-1 min-h-0 w-full max-w-full min-w-0 overflow-x-hidden overflow-y-visible overscroll-contain lg:overflow-hidden bg-muted/30 px-3 py-3 lg:px-3 xl:px-5 xl:py-4">
        <div className="mx-auto grid w-full max-w-[1440px] min-w-0 gap-4 lg:h-full lg:min-h-0 lg:grid-cols-[240px_minmax(0,1fr)] xl:grid-cols-[248px_minmax(0,1fr)_272px] 2xl:grid-cols-[280px_minmax(0,1fr)_320px]">


          {/* ══ LEFT RAIL (mobile: ABOVE the feed so Feeds nav is reachable) ══ */}
          <div className="order-1 space-y-4 min-w-0 lg:order-1 lg:h-full lg:min-h-0 lg:overflow-y-auto lg:pr-1">


            {/* Identity — REAL data for the logged-in user (Hub-isolated). */}
            <MyHubProfileCard />


            {/* Feeds (topic channels — replaces the old static Communities box) */}
            <FeedsNavCard
              feeds={feeds}
              selectedSlug={selectedFeedSlug}
              onSelect={selectFeed}
            />


            {/* Quick Post */}
            <Card>
              <CardHead title="Quick Post" />
              <div className="space-y-1.5 px-3 pb-3.5">
                {QUICK_SHARE.map((q) => (
                  <button
                    key={q.label}
                    type="button"
                    onClick={() =>
                      setComposerPrefill({ body: q.body, content_type: q.contentType })
                    }
                    className={cn(
                      "flex w-full items-center gap-2.5 rounded-lg border px-3 py-2.5 text-[12px] font-semibold transition-colors",
                      q.tone === "teal"
                        ? "border-brand-teal/25 bg-brand-teal-subtle/60 text-brand-teal-deep hover:bg-brand-teal-subtle"
                        : "border-brand-blue/25 bg-brand-blue/10 text-brand-blue hover:bg-brand-blue/15"
                    )}
                  >
                    <q.icon className="h-4 w-4 flex-shrink-0" />
                    {q.label}
                  </button>
                ))}
              </div>
            </Card>

            {/* Recently active (real Hub activity) */}
            <RecentlyActiveCard />


          </div>

          {/* ══ CENTER FEED (the only scrolling region on lg+) ══ */}
          <div className="order-2 space-y-4 min-w-0 lg:order-2 lg:h-full lg:min-h-0 lg:overflow-y-auto lg:pr-1">
            {/* Slim header reflecting the selected feed */}
            <FeedHeaderCard feed={selectedFeed} />
            {/* Composer + live hub_posts feed (Hub-isolated). */}
            <HubFeed
              feedId={selectedFeed?.id ?? null}
              feedSlug={selectedFeed?.slug ?? null}
              prefill={composerPrefill}
              onPrefillApplied={() => setComposerPrefill(null)}
              focusPostId={focusPostId}
              onFocusHandled={() => setFocusPostId(null)}
            />

          </div>


          {/* ══ RIGHT RAIL (mobile: BELOW the feed; hidden only at lg where the
              grid is 2-col; stationary + internally scrolling on xl+) ══ */}
          <div className="order-3 space-y-4 min-w-0 block lg:hidden xl:block xl:h-full xl:min-h-0 xl:overflow-y-auto xl:pr-1">


            {/* Recent Hub activity (real hub_posts — display only) */}
            <RecentActivityCard />

            {/* Recent Comments (Hub-isolated: hub_post_comments only) */}
            <RecentCommentsCard />

            {/* Trending tags (Hub-isolated: hub_posts.tags only) */}
            <TrendingTagsCard />

            {/* House ad slot #2 — rotating/swappable (separate from the Google Play card) */}
            <HubAdSlot />


          </div>
        </div>
      </div>
    </div>
  );
}
